import './gefen-programs.scss';
import React, { useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useOutletContext } from 'react-router';

import * as images from './gefen-programs-media';
import StopMotionSvg from '../../components/StopMotionSvg';
import DashedDrawedSvg from '../../components/DashedDrawedSvg';
import CommonButton from '../../components/button/button';
import useDeviceDetection from '../../hooks/useDeviceDetection';
import { AnimatePresence } from 'framer-motion';
import useIsRTL from '../../hooks/use-is-rtl';

function GefenPrograms() {
  const { reloadState, relativeVh } = useOutletContext();
  const [t, isRtl] = useIsRTL();
  const { pathname, state } = useLocation();
  const programsTypesRef = useRef(null);


  const deviceType = useDeviceDetection();

  useLayoutEffect(() => {
    reloadState[1](false);

    if (state?.dontReload || pathname?.includes('programs'))
      programsTypesRef?.current?.scrollIntoView({ behavior: 'auto' });
    else
      window.scrollTo({ top: 0 });

  }, []);

  useGSAP(() => {
    gsap.to('#gefen-programs-header', {
      scrollTrigger: {
        trigger: '#section1',
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
      scale: 1.21,
      x: `${!deviceType?.isMobile ? '-=70' : '+=10'}`
    });

    const tl = gsap.timeline();
    ScrollTrigger.create({
      animation: tl,
      trigger: '.section3',
      start: 'top 70%',
      end: '10% 20%',
      scrub: true,
    });
    tl.fromTo(
      '.section3 .row .item',
      {
        autoAlpha: 0,
        y: 100,
      },
      {
        autoAlpha: 1,
        y: 0,
        duration: 1,
        stagger: 0.35,
      }
    );


    //section 6 arrow


    if (!deviceType?.isMobile) {
      gsap.timeline({ repeat: -1 })
        .set('.arrow', { rotate: 0, x: 0, y: 0 }, 0)
        .set('.arrow', { rotate: 30, x: 5, y: 6 }, 0.6)
        .set('.arrow', { rotate: 60, x: -10, y: -16 }, 1.2)
        .set('.arrow', { rotate: 30, x: 5, y: 6 }, 1.8)
        .set('.arrow', { rotate: 0, x: 0, y: 0 }, 2.4);
    }

    else {
      gsap.timeline({ repeat: -1 })
        .to('a.related-image', { scale: 1.06, duration: 1.2 })
        .to('a.related-image', { scale: 1, duration: 1.2 }, '+=0.05')
    }

    // refresh after a short delay
    setTimeout(() => {
      ScrollTrigger.refresh();
    }, 100);
  }, [relativeVh]);



  return (
    <>
      <div className='gefen-programs-component'>

        {/* SECTION 1 */}
        <section id="section1" className="section1">
          <h1 id="gefen-programs-header" className='headline'>
            <Trans i18nKey='gefen.Gfen programs'>
              <span className='intro-title'>Gfen</span>programs
            </Trans>
          </h1>
        </section>

        {/* SECTION 2 */}
        <section id="section2" className='section2'>
          <p className='sub-title'>
            <Trans i18nKey='gefen.You can do so many beautiful things with the Gfen budget'>
              Meet the <span className='marked-text'>Gfen program</span>
            </Trans>
            <br />{t('gefen.variate')}
          </p>
          <img src={images.section2WineCup} alt='' />
        </section>

        {deviceType?.isMobile && (
          <div className="mobile-dash-line" id="mobile-dash-line-1">
            <DashedDrawedSvg
              svgWidth="243"
              svgHeight="202"
              d="M29.9009 4.884C47.8623 6.15313 192.969 61.0958 144.576 82.8937C33.9663 132.717 259.171 163.562 188.131 198.367"
              strokeDasharray={382}
              maskStrokDashArrayc='11'
              strokeWidth={11}
              trigger='#mobile-dash-line-1'
              startTrigger="top 85%"
              endTrigger="bottom 15%"
            />
          </div>
        )}

        {/* SECTION 3 */}
        <section id="section3" className='section3'>
          <div className='sub-title' ref={programsTypesRef}>
            <p className={'text-run'}>
              {t('gefen.How will you know which program to harvest to bring the finest yield to your school, one that creates a significant and lasting impact')}
            </p>
            <Trans i18nKey='gefen.Meet the Gfen program series by Lnet'>
              Meet the <span className='marked-text'>Gfen program</span>
            </Trans>
            <div className='text-run'>
              {t('gefen.And yes you can taste it click')}
              {images.section3Arrow}
            </div>
          </div>
          <div className='productions-types' id='productions-types'>
            <div className="row">
              {images.section3Images.map((item, index) => (
                <div className="item" key={`item${index}`}>
                  <Link to={`programs/${item.name}`}
                    state={{ dontReload: true }} className={`related-image ${item.name}`}>
                    <img src={item.img} alt={t(`gefen.section-2.${item.description}`)} />
                    <img src={item.imgHover} className='hover' alt={t(`gefen.section-2.${item.description}`)} />
                  </Link>
                  <p className={`${item.name}-p text-run`}>
                    {t(`gefen.section-2.${item.description}`)}
                  </p>
                  <div className='extended-description'>
                    {deviceType?.isMobile ? item.extendedDescriptionBackground : images.section3ExtendedDescriptionBackground}
                    <p className={`${item.name}-e`}>
                      <Trans i18nKey={`gefen.section-2.${item.extendedDescription}`}><b></b></Trans>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 5 */}
        {deviceType?.isMobile ? (
          <div className="mobile-dash-line" id="mobile-dash-line-2">
            <DashedDrawedSvg
              svgWidth="195"
              svgHeight="202"
              d="M13.5282 54.1537C38.9556 0.88418 60.5127 163.238 97.221 79.0132C113.281 42.164 182.436 140.913 182.649 157.338"
              strokeDasharray={340}
              maskStrokDashArrayc='11'
              strokeWidth={11}
              trigger='#mobile-dash-line-2'
              startTrigger="top 95%"
              endTrigger="bottom 10%"
            />
          </div>
        ) : (
          <section id="section5" className='section5'>
            <div className='transition-1' id="transition-1">
              <DashedDrawedSvg
                svgWidth="305"
                svgHeight="261"
                strokeDasharray={490}
                d="M5 23.5626C66.1959 -71.0616 67.224 241.606 157.364 91.1387C196.801 25.3077 303.532 229.217 299.91 260.366"
                trigger='#section5'
                startTrigger="top 85%"
                endTrigger="bottom 10%"
              />
            </div>
          </section>
        )}

        {/* SECTION 7 */}
        <section id="section7" className='section7 flex'>
          <p className={'text-run'}>
            {t('gefen.Our programs have years of proven success')}
          </p>
          <div className='media-container'>
            <img src={deviceType?.isMobile ? images.section7GoodluckImgMobile : images.section7GoodluckImg} alt='' />
            {deviceType?.isMobile ? (
              <div className="mobile-dash-line" id="mobile-dash-line-3">
                <DashedDrawedSvg
                  svgWidth="177"
                  svgHeight="161"
                  d="M29.6617 4.55018C43.7496 9.75225 187.614 98.0521 153.186 102.582C74.4966 112.937 175.892 147.787 125.746 156.501"
                  strokeDasharray={340}
                  maskStrokDashArrayc='11'
                  strokeWidth={11}
                  trigger='#mobile-dash-line-3'
                  startTrigger="top 85%"
                  endTrigger="bottom 10%"
                />
              </div>
            ) : (
              <div className='transition-2' id="transition-2">
                <DashedDrawedSvg
                  maskStrokDashArrayc='17 17'
                  svgWidth="88"
                  svgHeight="171"
                  strokeDasharray={300}
                  d="M73.5292 4.63264C-22.864 25.1329 172.777 68.3111 26.0937 96.8823C-38.0817 109.382 60.6697 164.071 77.2006 166.602"
                  trigger='#transition-2'
                  startTrigger="top 85%"
                  endTrigger="bottom 10%"
                />
              </div>
            )}
          </div>
        </section>

        {/* SECTION 10 */}
        <section id="section10" className='section10 flex-column'>
          <p className={'text-run'}>
            {t('gefen.pedagogis-exelence')}
          </p>
          {deviceType?.isMobile ?
            <img src={images.section10FeedbackImgMobile} alt='' style={{ width: '100%', alignSelf: "center", height: "auto", maxWidth: 'unset' }} />
            : <img src={images.section10FeedbackImg} alt='' />}
        </section>



        {deviceType?.isMobile &&
          <div className="mobile-dash-line" id="mobile-dash-line-4">
            <DashedDrawedSvg svgWidth="195" svgHeight="202"
              d="M181.472 54.1537 
              C156.044 0.88418 134.487 163.238 97.779 79.0132 
              C81.7187 42.164 12.5643 140.913 12.3514 157.338"
              strokeDasharray={340}
              maskStrokDashArrayc='11'
              strokeWidth={11}
              trigger='#mobile-dash-line-4'
              startTrigger="top 85%"
              endTrigger="bottom 10%"
            />
          </div>}

        {/* SECTION 11 */}
        <section id="section11" className='section11 flex'>
          <div className='flex-column'>
            <p className={'text-run'}>
              {t('gefen.They are delivered by professionals with excellent methodologies')}
            </p>
            {!deviceType?.isMobile && (
              <div className='transition-11' id="transition-11">
                <DashedDrawedSvg
                  svgWidth="179"
                  svgHeight="402"
                  strokeDasharray={800}
                  d="M117.5 4.77769C232.692 59.8465 -21.909 200.594 156.454 279.628C234.489 314.205 27.8363 395.241 0.999833 396.778"
                  trigger='#section11'
                  startTrigger="top 85%"
                  endTrigger="bottom 10%"
                />
              </div>
            )}
          </div>
          <div className='flex'>
            <img src={images.section11Media.img} alt='' />
            <StopMotionSvg
              shapes={images.section11Media.lines}
              width={295}
              height={430}
              interval={500}
            />
          </div>
          {deviceType?.isMobile && (
            <div className="mobile-dash-line" id="mobile-dash-line-5">
              <DashedDrawedSvg
                svgWidth="195"
                svgHeight="202"
                d="M13.5282 54.1537C38.9556 0.88418 60.5127 163.238 97.221 79.0132C113.281 42.164 182.436 140.913 182.649 157.338"
                strokeDasharray={340}
                maskStrokDashArrayc='11'
                strokeWidth={11}
                trigger='#mobile-dash-line-5'
                startTrigger="top 85%"
                endTrigger="bottom 10%"
              />
            </div>
          )}
        </section>

        {/* SECTION 12 */}
        <section id="section12" className='section12 flex'>
          <img src={images.section12Img3} alt='' />
          <p className={'text-run'}>
            {t('gefen.And use innovative technological platforms with support and operation services')}
          </p>
        </section>
        {deviceType?.isMobile && (
          <div className="mobile-dash-line" id="mobile-dash-line-9">
            <DashedDrawedSvg svgWidth="193" svgHeight="173"
              d="M153.611 9.32612C203.235 41.2898 39.4584 42.1295 118.361 89.2005C152.882 109.795 46.1754 165.897 29.8558 164.03"
              strokeDasharray={340}
              maskStrokDashArrayc='11'
              strokeWidth={11}
              trigger='#mobile-dash-line-9'
              startTrigger="top 85%"
              endTrigger="bottom 10%"
            />
          </div>
        )}

        {/* SECTION 13 */}
        <section id="section13" className='section13'>
          <div className='yalla'>
            <div className='sub-title'>
              <Trans
                i18nKey={
                  deviceType?.isMobile
                    ? 'gefen.mobile canclusion'
                    : "gefen.In short... don’t buy a cluster in a sack... to create meaningful change in the school, you need to choose the right programs!"
                }
              >
                In short you focus on the content and we will <span className='marked-text'>take care</span>
              </Trans>
            </div>
            <div className='flex'>
              <CommonButton text={t('gefen.I feel like tasting!')} routePath='/contact-us' />
              {images.section13Decorations.lines}
            </div>
          </div>
          <div className='section13-images'>
            <img src={images.section13Decorations.image} alt='' />
          </div>
        </section>

      </div>
      <AnimatePresence mode='popLayout'>
        <Outlet context={{ reloadState, relativeVh }} />
      </AnimatePresence>
    </>
  );


}

export default GefenPrograms;
